import React from 'react';
import { InvoiceItem } from '../../types';

interface CurrencyOption {
  value: string;
  label: string;
}

interface InvoiceItemsTableProps {
  invoiceItems: InvoiceItem[];
  currencyOptions: CurrencyOption[];
  onAddItem: () => void;
  onRemoveItem: (id: string) => void;
  onUpdateItem: (id: string, field: keyof InvoiceItem, value: any) => void;
}

const InvoiceItemsTable: React.FC<InvoiceItemsTableProps> = ({
  invoiceItems,
  currencyOptions,
  onAddItem,
  onRemoveItem,
  onUpdateItem,
}) => {
  return (
    <div className="border-t pt-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Invoice Items</h3>
        <button
          type="button"
          onClick={onAddItem}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
        >
          + Add Item
        </button>
      </div>

      {invoiceItems.length === 0 && (
        <p className="text-sm text-gray-500 italic mb-4">No items yet. Click "Add Item" or the subtotal will be used as a single item.</p>
      )}

      {invoiceItems.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-300">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 border-b text-xs font-medium text-gray-900 text-left">Description</th>
                <th className="px-3 py-2 border-b text-xs font-medium text-gray-900">From Date/Time</th>
                <th className="px-3 py-2 border-b text-xs font-medium text-gray-900">To Date/Time</th>
                <th className="px-3 py-2 border-b text-xs font-medium text-gray-900">Qty (Days)</th>
                <th className="px-3 py-2 border-b text-xs font-medium text-gray-900">Amount</th>
                <th className="px-3 py-2 border-b text-xs font-medium text-gray-900">Currency</th>
                <th className="px-3 py-2 border-b text-xs font-medium text-gray-900">Total</th>
                <th className="px-3 py-2 border-b text-xs font-medium text-gray-900">Action</th>
              </tr>
            </thead>
            <tbody>
              {invoiceItems.map((item) => (
                <tr key={item.id}>
                  <td className="px-2 py-2 border-b">
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => onUpdateItem(item.id, 'description', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      placeholder="Description"
                    />
                  </td>
                  <td className="px-2 py-2 border-b">
                    <input
                      type="datetime-local"
                      value={item.fromDate || ''}
                      onChange={(e) => onUpdateItem(item.id, 'fromDate', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                  </td>
                  <td className="px-2 py-2 border-b">
                    <input
                      type="datetime-local"
                      value={item.toDate || ''}
                      onChange={(e) => onUpdateItem(item.id, 'toDate', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                  </td>
                  <td className="px-2 py-2 border-b">
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => onUpdateItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                      className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                    />
                  </td>
                  <td className="px-2 py-2 border-b">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.amount}
                      onChange={(e) => onUpdateItem(item.id, 'amount', parseFloat(e.target.value) || 0)}
                      className="w-28 px-2 py-1 border border-gray-300 rounded text-sm"
                      placeholder="0.00"
                    />
                  </td>
                  <td className="px-2 py-2 border-b">
                    <select
                      value={item.currency || ''}
                      onChange={(e) => onUpdateItem(item.id, 'currency', e.target.value)}
                      className="w-24 px-2 py-1 border border-gray-300 rounded text-sm"
                    >
                      <option value="">-</option>
                      {currencyOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.value}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-2 border-b text-sm text-center font-medium">
                    {Number(item.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-2 py-2 border-b text-center">
                    <button
                      type="button"
                      onClick={() => onRemoveItem(item.id)}
                      className="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-50 font-medium">
                <td colSpan={6} className="px-3 py-2 border-t text-right text-sm">Items Subtotal:</td>
                <td className="px-3 py-2 border-t text-center text-sm">
                  {invoiceItems.reduce((sum, item) => sum + (item.total || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </td>
                <td className="px-3 py-2 border-t"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

export default InvoiceItemsTable;
